import { INode } from './INode';
import { AnswerMap } from '../AnswerMap';
import { OptionalText } from './OptionalText';
import { Edge } from './Edge';
import { Condition } from './Condition';
import { FriendlyAnswer } from '../FriendlyAnswer';
import { StringMap } from '../i18n';


export class Node {
  id: string;

  attribute: string | undefined;

  type: string;

  text: string;

  helperText: string;

  optionalHelperText: OptionalText[];

  detailText: string;

  defaultNext: string;

  condition: Condition | null = null;

  optionalText: OptionalText[];

  description: string;

  optionalDescription: OptionalText[];

  summaryHelperText: string;

  optionalSummaryHelperText: OptionalText[];

  edges: Edge[];

  hideFromSummary: boolean;

  public static readonly CHECKBOX_TYPE: string = 'checkbox';

  constructor(sourceNode: INode) {
    this.id = sourceNode.id;
    this.attribute = sourceNode.attribute;
    this.type = sourceNode.type;
    this.text = sourceNode.text;
    this.helperText = sourceNode.helperText;
    this.detailText = sourceNode.detailText;
    this.defaultNext = sourceNode.defaultNext;

    if (sourceNode.condition) {
      this.condition = new Condition(sourceNode.condition, sourceNode.orCondition);
    }

    this.description = sourceNode.description;
    this.summaryHelperText = sourceNode.summaryHelperText;
    this.edges = Edge.convertArray(sourceNode.edges);
    this.hideFromSummary = sourceNode.hideFromSummary;

    this.optionalText = OptionalText.convertArray(sourceNode.optionalText);
    this.optionalDescription = OptionalText.convertArray(sourceNode.optionalDescription);
    this.optionalSummaryHelperText = OptionalText.convertArray(sourceNode.optionalSummaryHelperText);
    this.optionalHelperText = OptionalText.convertArray(sourceNode.optionalHelperText);
  }

  public getNextNode(answers: AnswerMap): string | null {
    // If we don't meet the condition for this question, then we just go to the default next one.
    if (this.defaultNext && !this.evaluateCondition(answers)) {
      return this.defaultNext;
    }

    // If we don't have an answer for this question, then we can't proceed.
    if (answers[this.id] === undefined || answers[this.id] === null) {
      return null;
    }

    // Otherwise, get the edge that matches the answer
    const nextEdge: Edge | null = this.getEdgeForAnswer(answers[this.id]);

    if (nextEdge !== null && nextEdge.next) {
      return nextEdge.next;
    }

    if (this.defaultNext) {
      return this.defaultNext;
    }
    return null;
  }

  public isTerminalNode(answer: string | string[] | boolean): boolean {
    let terminal: boolean = true;
    for (let i = 0; i < this.edges.length; i++) {
      if (this.edges[i].id === answer || (!Array.isArray(this.edges[i].id) && Array.isArray(answer) && answer.length === 1 && answer[0] === this.edges[i].id)) {
        terminal = this.edges[i].next === null;
        break;
      }
    }
    return terminal;
  }

  public evaluateCondition(answers: AnswerMap): boolean {
    if (!this.condition) {
      return true;
    }

    return this.condition.evaluate(answers);
  }

  public getEdgeForAnswer(answer: string | string[] | boolean): Edge | null {
    for (let i = 0; i < this.edges.length; i++) {
      if (this.edges[i].id === answer) {
        return this.edges[i];
      }
    }

    return null;
  }

  public getEdgesForAnswers(answer: string[]): Edge[] {
    const edges: Edge[] = [];
    for (let i = 0; i < this.edges.length; i++) {
      if (answer.includes(this.edges[i].id as string)) {
        edges.push(this.edges[i]);
      }
    }

    return edges;
  }

  public toFriendlyAnswer(answerMap: AnswerMap, fiSettings: AnswerMap, stringMap: StringMap): FriendlyAnswer {
    const friendlyAnswerText: string[] = [];

    const answer: string | string[] | boolean | undefined = answerMap[this.id];

    if (answer != null) {
      if (this.type === Node.CHECKBOX_TYPE) {
        for (let i = 0; i < this.edges.length; i++) {
          if (Array.isArray(answer) && answer.includes(this.edges[i].id as string)) {
            friendlyAnswerText.push(stringMap[this.edges[i].getText(fiSettings)]);
          }
        }
      } else {
        for (let i = 0; i < this.edges.length; i++) {
          if (this.edges[i].id === answer) {
            friendlyAnswerText.push(stringMap[this.edges[i].getText(fiSettings)]);
          }
        }
      }
    }

    return {
      question: stringMap[this.getText(fiSettings)],
      answers: friendlyAnswerText,
    };
  }

  public getText(settings: AnswerMap): string {
    return OptionalText.evaluateOptionalText(settings, this.optionalText, this.text);
  }

  public getDescription(settings: AnswerMap): string {
    return OptionalText.evaluateOptionalText(settings, this.optionalDescription, this.description);
  }

  public getHelperText(settings: AnswerMap): string {
    return OptionalText.evaluateOptionalText(settings, this.optionalHelperText, this.helperText);
  }

  public getSummaryHelperText(settings: AnswerMap): string {
    return OptionalText.evaluateOptionalText(settings, this.optionalSummaryHelperText, this.summaryHelperText);
  }
}

export default Node;
